import { readEntitlementFailure, entitlementMessageKey } from '@/lib/entitlement-failure';
import type { ApiError } from '@/lib/api/types';
import { normalizeApiError } from '@/lib/api/types';
import { translateClient } from '@/lib/i18n/client';
import mediaSourceErrors from '../generated/media-source-errors.json';
import { TEMPLATE_ALIGNMENT_ERROR_CODES, templateAlignmentQuestions } from './template-alignment-error';

export type ErrorDisplayContext =
  | 'generic'
  | 'payment'
  | 'subscription'
  | 'credits'
  | 'generation'
  | 'upload'
  | 'asset'
  | 'canvas'
  | 'workflow'
  | 'auth'
  | 'share'
  | 'admin';

export interface UserFacingErrorCopy {
  title: string;
  message: string;
  actionLabel?: string;
  retryable: boolean;
  severity: 'info' | 'warning' | 'error';
  requestId?: string;
}

const CODE_KEY_BY_CODE: Record<string, string> = {
  cinema_settings_invalid: 'cinemaSettingsInvalid',
  cinema_settings_requires_image_creation: 'cinemaSettingsRequiresImage',
  cinema_settings_unsupported_workflow: 'cinemaSettingsUnsupportedWorkflow',
  cinema_settings_reference_authority_conflict: 'cinemaSettingsReferenceConflict',
  SOUL_ID_REQUIRES_SOUL_2: 'soulIdRequiresSoul2',
  SOUL_ID_NOT_READY: 'soulIdNotReady',
  SOUL_ID_ACCOUNT_CHANGED: 'soulIdAccountChanged',
  template_alignment_needs_input: 'templateAlignmentNeedsInput',
  template_alignment_invalid: 'templateAlignmentInvalid',
  conversation_skill_not_supported: 'conversationSkillNotSupported',
  pose_reference_output_count_mismatch: 'validation',
  pose_reference_ids_duplicate: 'validation',
  pose_reference_lookbook_required: 'validation',
  pose_reference_selection_changed: 'conflict',
  pose_reference_entry_unavailable: 'conflict',
  pose_reference_not_owned_or_ready: 'conflict',
  pose_reference_ids_invalid: 'validation',
  pose_reference_subject_required: 'templateAlignmentNeedsInput',
  pose_reference_subject_changed: 'conflict',
  lookbook_source_roles_character_conflict: 'lookbookSourceCharacterConflict',
  lookbook_source_roles_subject_required: 'lookbookSourceSubjectRequired',
  lookbook_source_roles_attachment_required: 'lookbookSourceAttachmentRequired',
  lookbook_source_roles_source_changed: 'lookbookSourceAttachmentRequired',
  lookbook_source_roles_selection_changed: 'lookbookSourceSelectionChanged',
  lookbook_source_roles_duplicate: 'lookbookSourceSelectionChanged',
  lookbook_source_roles_invalid: 'lookbookSourceSelectionChanged',
  lookbook_source_roles_lookbook_required: 'lookbookSourceLookbookRequired',
  KAGEMANE_DEPTH_UNSUPPORTED: 'kagemaneDepthUnsupported',
  KAGEMANE_DEPTH_UNAVAILABLE: 'kagemaneDepthUnavailable',
  KAGEMANE_TEMPLATE_NOT_FOUND: 'kagemaneTemplateNotFound',
  KAGEMANE_HEYGEN_UNAVAILABLE: 'kagemaneHeygenUnavailable',
  KAGEMANE_HEYGEN_LIMITS: 'kagemaneHeygenLimits',
  KAGEMANE_HEYGEN_RATIO: 'kagemaneHeygenRatio',
  KAGEMANE_MODEL_RESOLUTION: 'kagemaneModelResolution',
  KAGEMANE_GIFT_UNAVAILABLE: 'kagemaneGiftUnavailable',
  KAGEMANE_GENJUTSU_MEDIA_SIZE: 'kagemaneGenjutsuMediaSize',
  KAGEMANE_GENJUTSU_SOURCE_PIXELS: 'kagemaneGenjutsuSourcePixels',
  KAGEMANE_GENJUTSU_LIMITS: 'kagemaneGenjutsuLimits',
  KAGEMANE_GENJUTSU_PRESET_CHANGED: 'kagemaneGenjutsuPresetChanged',
  KAGEMANE_GENJUTSU_QUOTE_CHANGED: 'kagemaneGenjutsuQuoteChanged',
  KAGEMANE_GENJUTSU_UNAVAILABLE: 'kagemaneGenjutsuUnavailable',
  KAGEMANE_GENJUTSU_FAILED: 'kagemaneGenjutsuFailed',
  KAGEMANE_GENJUTSU_MODEL: 'kagemaneGenjutsuModel',
  KAGEMANE_TEMPLATE_CHANGED: 'kagemaneTemplateChanged',
  KAGEMANE_TEMPLATE_MATERIALS: 'kagemaneTemplateMaterials',
  UGC_WORKFLOW_RETIRED: 'ugcWorkflowRetired',
  HYPIT_PRODUCTION_FAILED: 'hypitProductionFailed',
  HYPIT_AUTHORING_FAILED: 'hypitAuthoringFailed',
  HYPIT_AUTHORING_TIMEOUT: 'hypitAuthoringTimeout',
  HYPIT_CONTEXT_LIMIT: 'hypitContextLimit',
  HYPIT_SOURCE_APPEARANCE_CAPACITY_9: 'hypitSourceAppearanceCapacity9',
  HYPIT_SOURCE_APPEARANCE_CAPACITY_10: 'hypitSourceAppearanceCapacity10',
  HYPIT_PROVIDER_TIMEOUT: 'hypitProviderTimeout',
  HYPIT_PROVIDER_FAILED: 'hypitProviderFailed',
  HYPIT_PROVIDER_REQUEST_REJECTED: 'hypitProviderRequestRejected',
  HYPIT_REFERENCE_IMAGE_UNAVAILABLE: 'hypitReferenceImageUnavailable',
  HYPIT_REFERENCE_MEDIA_PIXELS: 'hypitReferenceMediaPixels',
  HYPIT_BUILD_FAILED: 'hypitBuildFailed',
  HYPIT_UNSUPPORTED_REQUEST: 'hypitUnsupportedRequest',
  HYPIT_REVIEW_FAILED: 'hypitReviewFailed',
  HYPIT_RUNTIME_UNAVAILABLE: 'hypitRuntimeUnavailable',
  HYPIT_REFERENCE_DURATION: 'hypitReferenceDuration',
  HYPIT_REFERENCE_ROLES: 'hypitReferenceRoles',
  REFERENCE_IMAGE_IMPORT_FAILED: 'referenceImageImportFailed',
  HYPIT_PROJECT_MISSING: 'hypitProjectMissing',
  VALIDATION_ERROR: 'validation',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'notFound',
  CONFLICT: 'conflict',
  RATE_LIMITED: 'rateLimited',
  INSUFFICIENT_CREDITS: 'insufficientCredits',
  PAYMENT_REQUIRED: 'paymentRequired',
  FEATURE_SUBSCRIPTION_REQUIRED: 'featureSubscriptionRequired',
  PAID_SUBSCRIPTION_REQUIRED: 'paidSubscriptionRequired',
  WEB_SUBSCRIPTION_REQUIRED: 'paidSubscriptionRequired',
  WECHAT_OPENID_REQUIRED: 'wechatOpenidRequired',
  APPLE_APP_SUBSCRIPTION_WEB_CHECKOUT_BLOCKED: 'conflict',
  APPLE_AUTO_RENEW_MUST_BE_DISABLED: 'conflict',
  AIRWALLEX_PLAN_CHANGE_REQUIRES_SUPPORT: 'airwallexPlanChange',
  AIRWALLEX_PLAN_CHANGE_PENDING: 'airwallexPlanChangePending',
  AIRWALLEX_SUBSCRIPTION_ID_MISSING: 'airwallexSubscriptionSync',
  AIRWALLEX_SUBSCRIPTION_SYNC_FAILED: 'airwallexSubscriptionSync',
  AIRWALLEX_CHECKOUT_UNAVAILABLE: 'airwallexCheckoutUnavailable',
  AIRWALLEX_CHECKOUT_RECOVERY_UNAVAILABLE: 'airwallexCheckoutUnavailable',
  AIRWALLEX_CHECKOUT_IN_PROGRESS: 'airwallexCheckoutInProgress',
  AIRWALLEX_PAYMENT_INSUFFICIENT_FUNDS: 'airwallexPaymentInsufficientFunds',
  AIRWALLEX_PAYMENT_CARD_EXPIRED: 'airwallexPaymentCardExpired',
  AIRWALLEX_PAYMENT_INVALID_SECURITY_CODE: 'airwallexPaymentInvalidSecurityCode',
  AIRWALLEX_PAYMENT_DECLINED: 'airwallexPaymentDeclined',
  AIRWALLEX_PAYMENT_SETUP_FAILED: 'airwallexPaymentSetupFailed',
  AIRWALLEX_PAYMENT_REQUEST_REJECTED: 'airwallexPaymentRequestRejected',
  AIRWALLEX_PAYMENT_CANCELLATION_PENDING: 'airwallexCancellationPending',
  AIRWALLEX_PAYMENT_CANCELLATION_UNAVAILABLE: 'airwallexCancellationUnavailable',
  SUBSCRIPTION_PRICE_VARIANT_NOT_AVAILABLE: 'subscriptionOfferUnavailable',
  ORDER_SUBSCRIPTION_VARIANT_CONFLICT: 'subscriptionOfferUnavailable',
  AIRWALLEX_AMOUNT_LIMIT_EXCEEDED: 'airwallexAmountLimit',
  SERVICE_UNAVAILABLE: 'serviceUnavailable',
  INTERNAL_ERROR: 'internal',
  NETWORK_ERROR: 'network',
  VERSION_CONFLICT: 'conflict',
  INVALID_IMAGE: 'invalidImage',
  upload_recovery_unavailable: 'uploadRecoveryUnavailable',
  material_upload_content_hash_mismatch: 'uploadIntegrityMismatch',
  material_upload_size_mismatch: 'uploadIntegrityMismatch',
  creative_execution_failed: 'creativeExecutionFailed',
  image_reference_limit_exceeded: 'imageReferenceCapacityBlocked',
  selected_reference_analysis_failed: 'selectedReferenceAnalysisFailed',
  style_classification_unavailable: 'styleClassificationUnavailable',
  production_parameters_need_clarification: 'productionParametersNeedClarification',
  continuation_source_target_required: 'continuationSourceTargetRequired',
  task_manifest_natural_plan_candidate_changed: 'planMaterialConflict',
  creative_media_submission_failed: 'creativeExecutionFailed',
  creative_generation_plan_not_submitted: 'creativeGenerationPlanNotSubmitted',
  creative_generation_not_submitted: 'creativeGenerationNotSubmitted',
  creative_generation_failed: 'creativeGenerationFailed',
  sse_admission_rejected: 'sseAdmissionRejected',
  creative_retry_nothing_failed: 'generationRetryNothingFailed',
  creative_retry_in_progress: 'generationRetryInProgress',
  task_manifest_camera_intent_binding_changed: 'retrySourceIdentityUnverified',
  generation_retry_nothing_failed: 'generationRetryNothingFailed',
  generation_retry_source_unavailable: 'generationRetryNothingFailed',
  generation_retry_in_progress: 'generationRetryInProgress',
  insufficient_credits: 'generationBatchInsufficientCredits',
  generation_retry_contract_unavailable: 'generationRetryContractUnavailable',
  creative_generation_partially_failed: 'creativeGenerationPartiallyFailed',
  creative_admission_failed: 'creativeExecutionFailed',
  midjourney_output_count_fixed: 'midjourneyOutputCountFixed',
  midjourney_quality_unsupported: 'midjourneyQualityUnsupported',
  midjourney_hd_unsupported: 'midjourneyHdUnsupported',
  midjourney_draft_unsupported: 'midjourneyDraftUnsupported',
  midjourney_authority_workflow_unsupported: 'midjourneyAuthorityWorkflowUnsupported',
  midjourney_workflow_unsupported: 'midjourneyWorkflowUnsupported',
  midjourney_reference_role_unsupported: 'midjourneyReferenceRoleUnsupported',
  image_model_unavailable: 'imageModelUnavailable',
  design_extension_model_resolution: 'designExtensionModelResolution',
  text_model_unavailable: 'textModelUnavailable',
  text_model_catalog_unavailable: 'textModelCatalogUnavailable',
  seedream_parameters_invalid: 'seedreamParametersInvalid',
  canvas_binding_conflict: 'canvasBindingConflict',
  chat_turn_changed: 'chatTurnChanged',
  chat_turn_not_found: 'chatTurnNotFound',
  chat_turn_delete_failed: 'chatTurnDeleteFailed',
};

const ACTION_KEY_BY_ACTION: Record<string, string> = {
  retry: 'retry',
  login: 'login',
  top_up: 'topUp',
  check_access: 'checkAccess',
  fix_fields: 'fixFields',
  change_image_model: 'changeImageModel',
  refresh: 'refresh',
};

function isApiError(value: unknown): value is ApiError {
  return typeof value === 'object'
    && value !== null
    && 'code' in value
    && 'message' in value;
}

function errorFromUnknown(error: unknown, fallbackMessage?: string): ApiError {
  if (isApiError(error)) {
    return error;
  }
  return normalizeApiError(error, fallbackMessage ?? translateClient('errors', 'common.fallbackMessage'));
}

function translationExists(value: string, key: string) {
  return value !== key && value.trim().length > 0;
}

const INTERNAL_ERROR_DETAIL_PATTERN = /\b(?:model(?:_key)?|task_type|provider|transport|routing|route|seedance|seeddance|onedance|baidu|tencent|volcengine|volcano|volc|vod|ark|arts-api|image2|image[-_\s]?2|gpt[-_\s]?image|doubao|bytedance|hunyuan|kling|api[-_\s]?key)\b|任务类型|模型|模型key|分流|路由|服务商|供应商|通道|百度|腾讯|火山|方舟|豆包|可灵|即梦/i;

function sanitizeApiMessage(message: string | undefined, fallbackMessage?: string) {
  const normalized = String(message || '').trim();
  if (!normalized) {
    return fallbackMessage || translateClient('errors', 'common.fallbackMessage');
  }
  if (INTERNAL_ERROR_DETAIL_PATTERN.test(normalized)) {
    return fallbackMessage || translateClient('errors', 'common.fallbackMessage');
  }
  return normalized;
}

function continuationSourceLabels(details: ApiError['details']): string[] | undefined {
  const sources = details?.required_sources;
  if (!Array.isArray(sources) || sources.length === 0 || sources.length > 64) return undefined;
  const allowedKinds = new Set(['consultation_document', 'attached_node', 'source_selection']);
  const labels: string[] = [];
  for (const source of sources) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return undefined;
    const { input_kind: kind, input_ordinal: ordinal } = source as Record<string, unknown>;
    if (typeof kind !== 'string' || !allowedKinds.has(kind)
      || typeof ordinal !== 'number' || !Number.isSafeInteger(ordinal)
      || ordinal < 0 || ordinal >= 100000) return undefined;
    // Only the category's original ordinal is public display material. Global
    // indices, source IDs, URLs and model-authored labels stay diagnostics.
    labels.push(translateClient('errors', `codes.continuationSourceTargetRequired.sources.${kind}`, {
      number: ordinal + 1,
    }));
  }
  return [...new Set(labels)];
}

export function formatUserFacingError(
  error: unknown,
  context: ErrorDisplayContext = 'generic',
  fallbackMessage?: string,
): UserFacingErrorCopy {
  const apiError = errorFromUnknown(error, fallbackMessage);
  if (apiError.code === 'creative_retry_read_outcome_unknown') {
    return {
      title: translateClient('errors', 'codes.retryReadOutcomeUnknown.title'),
      message: translateClient('errors', 'codes.retryReadOutcomeUnknown.message'),
      actionLabel: translateClient('entitlements', 'contactSupport'),
      retryable: false, severity: 'warning', requestId: apiError.requestId,
    };
  }
  if (apiError.code === 'continuation_source_target_required') {
    const sources = continuationSourceLabels(apiError.details);
    return {
      title: translateClient('errors', 'codes.continuationSourceTargetRequired.title'),
      message: sources
        ? translateClient('errors', 'codes.continuationSourceTargetRequired.details', { sources: sources.join(', ') })
        : translateClient('errors', 'codes.continuationSourceTargetRequired.message'),
      actionLabel: translateClient('errors', 'actions.fixFields'),
      retryable: false, severity: 'warning', requestId: apiError.requestId,
    };
  }
  const failure = readEntitlementFailure(error);
  if (failure && !['payment', 'subscription', 'auth', 'admin'].includes(context)) {
    return { title: translateClient('entitlements', 'rejectedTitle'), message: translateClient('entitlements', entitlementMessageKey(failure)),
      actionLabel: failure.action === 'contact_support' || failure.code === 'WALLET_FROZEN' ? translateClient('entitlements', 'contactSupport') : undefined,
      retryable: failure.retryable === true, severity: 'warning', requestId: apiError.requestId };
  }
  if (Object.prototype.hasOwnProperty.call(mediaSourceErrors, apiError.code)) {
    const pending = apiError.code === 'media_source_not_ready';
    return { title: translateClient('mediaSourceErrors', 'title'),
      message: translateClient('mediaSourceErrors', apiError.code),
      actionLabel: translateClient('errors', pending ? 'actions.retry' : 'actions.fixFields'),
      retryable: pending && apiError.retryable === true, severity: 'warning', requestId: apiError.requestId };
  }
  // A received unrecognized 422 needs input correction, not blind resubmission.
  // Its private body remains diagnostic data; only a known code chooses copy.
  const statusKey: Record<number, string> = {401: 'unauthorized', 402: 'insufficientCredits',
    403: 'forbidden', 404: 'notFound', 409: 'conflict', 422: 'validation',
    429: 'rateLimited', 503: 'serviceUnavailable'};
  const status = apiError.details?.httpStatus;
  const codeKey = apiError.userMessageKey ?? CODE_KEY_BY_CODE[apiError.code]
    ?? (typeof status === 'number' ? statusKey[status] : undefined) ?? 'unknown';
  const titleKey = `codes.${codeKey}.title`;
  const messageKey = `codes.${codeKey}.message`;
  const contextMessageKey = `contexts.${context}.${codeKey}`;
  const translatedTitle = translateClient('errors', titleKey);
  const translatedContextMessage = translateClient('errors', contextMessageKey);
  const translatedMessage = translateClient('errors', messageKey);
  const shouldUseApiValidationMessage = apiError.code === 'VALIDATION_ERROR'
    && typeof apiError.message === 'string'
    && apiError.message.trim().length > 0;
  const shouldUseAuthErrorMessage = context === 'auth'
    && apiError.code === 'UNKNOWN_ERROR'
    && typeof apiError.message === 'string'
    && apiError.message.trim().length > 0;
  const directApiMessage = shouldUseApiValidationMessage
    ? apiError.message
    : shouldUseAuthErrorMessage
      ? apiError.message
      : undefined;
  let message: string;
  if (directApiMessage) {
    message = sanitizeApiMessage(directApiMessage, fallbackMessage);
  } else if (translationExists(translatedContextMessage, contextMessageKey)) {
    message = translatedContextMessage;
  } else if (translationExists(translatedMessage, messageKey)) {
    message = translatedMessage;
  } else {
    message = sanitizeApiMessage(apiError.message, fallbackMessage);
  }

  if (apiError.code === 'design_extension_model_resolution') {
    const details = apiError.details;
    const resolutions = Array.isArray(details?.supported_resolutions)
      ? details.supported_resolutions.filter((tier): tier is string => typeof tier === 'string').join(' / ')
      : '';
    if (typeof details?.model_id === 'string' && resolutions) {
      message = translateClient('errors', 'codes.designExtensionModelResolution.details', {
        model: details.model_id, resolutions,
        resolution: typeof details.required_resolution === 'string' ? details.required_resolution : '2K',
      });
    }
  }

  const isAlignment = TEMPLATE_ALIGNMENT_ERROR_CODES.has(apiError.code);
  if (apiError.code === 'template_alignment_needs_input') {
    const questions = templateAlignmentQuestions(apiError.details);
    if (questions.length) message += `\n${questions.join('\n')}`;
  }

  const actionKey = (isAlignment ? 'fixFields' : undefined) ?? apiError.actionKey
    ?? (apiError.action ? ACTION_KEY_BY_ACTION[apiError.action] : undefined)
    ?? (apiError.retryable ? 'retry' : undefined);
  const actionLabel = actionKey ? translateClient('errors', `actions.${actionKey}`) : undefined;

  return {
    title: translationExists(translatedTitle, titleKey)
      ? translatedTitle
      : translateClient('errors', 'common.title'),
    message,
    actionLabel: actionLabel && translationExists(actionLabel, `actions.${actionKey}`) ? actionLabel : undefined,
    retryable: !isAlignment && Boolean(apiError.retryable),
    severity: isAlignment || apiError.code === 'design_extension_model_resolution' ? 'warning' : apiError.severity ?? 'error',
    requestId: apiError.requestId,
  };
}

export function formatErrorToastMessage(error: unknown, context: ErrorDisplayContext = 'generic') {
  const copy = formatUserFacingError(error, context);
  const requestSuffix = copy.requestId
    ? ` ${translateClient('errors', 'common.requestIdShort', { requestId: copy.requestId })}`
    : '';
  return `${copy.title}: ${copy.message}${requestSuffix}`;
}

export function getUserFacingErrorMessage(
  error: unknown,
  context: ErrorDisplayContext = 'generic',
  fallbackMessage?: string,
) {
  return formatUserFacingError(error, context, fallbackMessage).message;
}
